import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Loader2, Receipt, ChevronRight, ChevronLeft } from "lucide-react";
import { api, errMsg, faDigits, faNum, fmtJalaliDate, tehranMidnightTs } from "../lib/api";
import InvoiceView from "../components/InvoiceView";

const card = "bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5";
const input = "w-full border rounded-xl px-3 py-2 text-sm bg-white";
const label = "text-xs text-slate-600 mb-1 block";

function ymd(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function AdminInvoices() {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState(location.state?.userId || "");
  const [start, setStart] = useState(() => ymd(new Date(Date.now() - 29 * 86400000)));
  const [end, setEnd] = useState(() => ymd(new Date()));
  const [discount, setDiscount] = useState(0);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);
  const pageSize = 20;

  useEffect(() => {
    api.get("/admin/users", { params: { page: 1, page_size: 100 } })
      .then(({ data }) => setUsers(data.items)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const loadList = useCallback(() => {
    api.get("/admin/invoices", { params: { page, page_size: pageSize } })
      .then(({ data }) => { setList(data.items); setTotal(data.total); })
      .catch((e) => toast.error(errMsg(e)));
  }, [page]);

  useEffect(() => { loadList(); }, [loadList]);

  const payload = () => ({
    user_id: userId,
    period_start: tehranMidnightTs(start),
    period_end: tehranMidnightTs(end) + 86400, // پایان روز انتخابی (انحصاری)
    discount: Number(discount) || 0,
  });

  const makePreview = async () => {
    if (!userId) return toast.error("کاربر را انتخاب کنید");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invoices/preview", payload());
      setPreview(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const issue = async () => {
    if (!preview) return;
    if (!window.confirm("پیش‌فاکتور صادر شود؟ پس از صدور، مبالغ آن ثابت می‌ماند.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invoices", payload());
      toast.success(`پیش‌فاکتور شماره ${faDigits(data.invoice_no)} صادر شد`);
      setPreview(null);
      setViewing(data);
      loadList();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const openInvoice = async (id) => {
    try {
      const { data } = await api.get(`/admin/invoices/${id}`);
      setViewing(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const shown = preview || viewing;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2"><Receipt size={20} /> پیش‌فاکتورها</h1>
        <p className="text-sm text-slate-500 mt-1">پیش‌فاکتور بر اساس استعلام‌های پاسخ‌داده‌شده و قیمت‌های هر کاربر در بازه انتخابی محاسبه می‌شود.</p>
      </div>

      <div className={card}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className={label}>کاربر</label>
            <select className={input} value={userId} onChange={(e) => { setUserId(e.target.value); setPreview(null); }}>
              <option value="">انتخاب کنید…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name ? `${u.name} · ` : ""}{u.mobile}</option>)}
            </select>
          </div>
          <div><label className={label}>از تاریخ (میلادی)</label><input type="date" className={input} value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><label className={label}>تا تاریخ (شامل)</label><input type="date" className={input} value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <div><label className={label}>تخفیف (ریال)</label><input type="number" min={0} className={input} value={discount} onChange={(e) => setDiscount(e.target.value)} /></div>
          <div className="flex items-end gap-2">
            <button onClick={makePreview} disabled={busy} className="flex-1 bg-slate-900 text-white text-sm px-4 py-2 rounded-xl flex items-center justify-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} پیش‌نمایش
            </button>
            {preview && (
              <button onClick={issue} disabled={busy} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white text-sm px-4 py-2 rounded-xl">صدور</button>
            )}
          </div>
        </div>
      </div>

      {shown && (
        <div className="space-y-2">
          {preview && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">این پیش‌نویس است و هنوز صادر نشده است.</div>}
          {viewing && !preview && <button onClick={() => setViewing(null)} className="text-sm text-teal-700 flex items-center gap-1"><ChevronRight size={16} /> بستن نمایش</button>}
          <InvoiceView invoice={shown} />
        </div>
      )}

      <div className={card}>
        <div className="font-medium mb-3">پیش‌فاکتورهای صادرشده ({faNum(total)})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 border-b">
                <th className="py-2 text-right font-medium">شماره</th>
                <th className="py-2 text-right font-medium">دوره</th>
                <th className="py-2 text-right font-medium">تاریخ صدور</th>
                <th className="py-2 text-right font-medium">مبلغ (ریال)</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">پیش‌فاکتوری صادر نشده است</td></tr>}
              {list.map((i) => (
                <tr key={i.id} className="border-b border-slate-100">
                  <td className="py-2">{faDigits(i.invoice_no)}</td>
                  <td className="py-2">{fmtJalaliDate(i.period_start)} تا {fmtJalaliDate(i.period_end - 1)}</td>
                  <td className="py-2">{fmtJalaliDate(i.issued_at)}</td>
                  <td className="py-2">{faNum(i.total)}</td>
                  <td className="py-2 text-left">
                    <button onClick={() => openInvoice(i.id)} className="text-teal-700 text-xs flex items-center gap-1"><Eye size={14} /> نمایش</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="flex items-center gap-1 disabled:text-slate-300"><ChevronRight size={16} /> قبلی</button>
          <span className="text-slate-500">صفحه {faDigits(page)} از {faDigits(pages)}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="flex items-center gap-1 disabled:text-slate-300">بعدی <ChevronLeft size={16} /></button>
        </div>
      </div>
    </div>
  );
}
